import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  STATUT_LABELS, STATUT_COLORS, CATEGORIE_LABELS,
  SCORE_NIVEAU_LABELS, SCORE_NIVEAU_COLORS, PRIORITE_NIVEAUX,
} from "@/lib/prospects";
import { ProspectDetail } from "@/components/ProspectDetail";
import { downloadExport } from "@/components/TemplatesDialog";
import { useAuth } from "@/context/AuthContext";
import { Phone, Mail, Copy, CheckCircle2, Flame, Sparkles, TrendingUp, ClipboardList, ChevronDown, Download, UserPlus } from "lucide-react";

const CLOSE_STATUTS = ["a_recontacter", "pas_interesse", "desinscrit", "contacte"];

const ScoreBadge = ({ lead }) => {
  const [open, setOpen] = useState(false);
  if (lead.score === undefined || lead.score === null) return null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${SCORE_NIVEAU_COLORS[lead.score_niveau] || "bg-secondary"}`}
        data-testid={`score-badge-${lead.id}`}
      >
        {lead.score}/100 · {SCORE_NIVEAU_LABELS[lead.score_niveau]} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute left-0 top-8 z-20 w-72 rounded-md border bg-card p-3 shadow-xl" data-testid={`score-details-${lead.id}`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Détail du score</p>
          <div className="mt-2 space-y-1 text-sm">
            {(lead.score_details || []).map((d, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span className="text-muted-foreground">{d.critere}</span>
                <span className={`font-medium ${d.points < 0 ? "text-red-600" : "text-green-700"}`}>{d.points > 0 ? "+" : ""}{d.points}</span>
              </div>
            ))}
          </div>
          {(lead.mots_cles_detectes || []).length > 0 && (
            <p className="mt-2 border-t pt-2 text-xs text-muted-foreground">Mots-clés : {lead.mots_cles_detectes.join(", ")}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function LeadsToday() {
  const { user } = useAuth();
  const canAssign = ["admin", "responsable_commercial"].includes(user?.role);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [closing, setClosing] = useState(null);
  const [closeForm, setCloseForm] = useState({ nouveau_statut: "a_recontacter", commentaire: "" });
  const [draftFor, setDraftFor] = useState(null);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [oppFor, setOppFor] = useState(null);
  const [oppForm, setOppForm] = useState({ valeur_potentielle: "", commentaire: "" });

  const load = () => api.get("/leads/today").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const assign = async (lead, commercialId) => {
    try {
      const { data: r } = await api.post(`/leads/${lead.id}/assign`, { commercial_id: commercialId });
      toast.success(r.message);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyText = (t, msg) => { navigator.clipboard.writeText(t); toast.success(msg); };

  const closeLead = async () => {
    try {
      await api.post(`/leads/${closing.id}/close`, closeForm);
      toast.success("Lead pris en main");
      setClosing(null);
      setCloseForm({ nouveau_statut: "a_recontacter", commentaire: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateDraft = async (lead) => {
    setDraftFor(lead);
    setDraft(null);
    setDrafting(true);
    try {
      const { data: d } = await api.post(`/prospects/${lead.id}/reply-draft`);
      setDraft(d);
    } catch (e) {
      toast.error(formatApiError(e));
      setDraftFor(null);
    } finally { setDrafting(false); }
  };

  const createOpp = async () => {
    try {
      await api.post(`/prospects/${oppFor.id}/opportunity`, {
        valeur_potentielle: parseFloat(oppForm.valeur_potentielle) || 0,
        commentaire: oppForm.commentaire,
      });
      toast.success("Opportunité créée — lead transmis au pipeline");
      setOppFor(null);
      setOppForm({ valeur_potentielle: "", commentaire: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeTask = async (t) => {
    try {
      await api.post(`/tasks/${t.id}/complete`);
      toast.success("Tâche terminée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const c = data?.compteurs;

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="leads-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display flex items-center gap-2 text-3xl font-semibold sm:text-4xl"><Flame className="text-accent" /> Leads à traiter aujourd'hui</h1>
          <p className="mt-1 text-sm text-muted-foreground">Uniquement ce qui vaut de l'argent : leads chauds, devis, appels demandés, réponses non traitées.</p>
        </div>
        {c && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge className="bg-red-600 text-white" data-testid="counter-p1">🔴 {c.priorite_1} en priorité 1</Badge>
            <Badge variant="secondary" data-testid="counter-overdue">{c.taches_en_retard} tâche(s) en retard</Badge>
            <Button size="sm" variant="outline" onClick={() => downloadExport("/export/leads.xlsx", "renforteo_leads.xlsx")} data-testid="export-leads-button">
              <Download size={14} className="mr-1" /> Excel
            </Button>
          </div>
        )}
      </div>

      {(data?.taches || []).length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="font-display flex items-center gap-2 text-lg font-semibold"><ClipboardList size={18} /> Tâches commerciales</h2>
            <div className="mt-3 space-y-2" data-testid="tasks-list">
              {data.taches.map((t) => (
                <div key={t.id} className={`flex flex-wrap items-center gap-2 rounded-md border p-2.5 text-sm ${t.en_retard ? "border-red-300 bg-red-50" : ""}`}>
                  {t.en_retard && <Badge variant="destructive">En retard</Badge>}
                  <span className="font-medium">{t.titre}</span>
                  <span className="text-muted-foreground">— {t.prospect_nom}</span>
                  <span className="ml-auto text-xs text-muted-foreground">Échéance : {new Date(t.echeance).toLocaleString("fr-FR")}</span>
                  <Button size="sm" variant="outline" onClick={() => completeTask(t)} data-testid={`task-complete-${t.id}`}>
                    <CheckCircle2 size={14} className="mr-1" /> Fait
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data === null ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : data.leads.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Aucun lead à traiter. Les réponses positives, demandes de devis et d'appel apparaîtront ici automatiquement.</CardContent></Card>
      ) : (
        <div className="space-y-4" data-testid="leads-list">
          {data.leads.map((l) => {
            const pn = PRIORITE_NIVEAUX[l.priorite_niveau || 4];
            return (
              <Card key={l.id} className="kpi-card border-l-4" style={{ borderLeftColor: l.priorite_niveau === 1 ? "hsl(0 72% 45%)" : l.priorite_niveau === 2 ? "hsl(25 90% 50%)" : l.priorite_niveau === 3 ? "hsl(45 90% 50%)" : "hsl(220 12% 80%)" }}>
                <CardContent className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-display text-lg font-semibold">{l.raison_sociale}</p>
                        <Badge className={pn.color} data-testid={`priority-badge-${l.id}`}>{pn.dot} {pn.label}</Badge>
                        <Badge className={STATUT_COLORS[l.statut]}>{STATUT_LABELS[l.statut]}</Badge>
                        <ScoreBadge lead={l} />
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {l.contact_prenom} {l.contact_nom} · {l.contact_fonction || "fonction inconnue"} · {l.ville}
                        {l.updated_at && <> · dernière interaction : {new Date(l.updated_at).toLocaleString("fr-FR")}</>}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {l.telephone && (
                        <>
                          <Button asChild size="sm" variant="outline" data-testid={`call-lead-${l.id}`}>
                            <a href={`tel:${l.telephone}`}><Phone size={14} className="mr-1.5" /> Appeler</a>
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => copyText(l.telephone, "Numéro copié")} title="Copier le numéro"><Copy size={14} /></Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => generateDraft(l)} data-testid={`draft-lead-${l.id}`}>
                        <Sparkles size={14} className="mr-1.5" /> Répondre
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setOppFor(l)} data-testid={`opp-lead-${l.id}`}>
                        <TrendingUp size={14} className="mr-1.5" /> Opportunité
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-md bg-secondary/60 p-3 text-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Réponse ({CATEGORIE_LABELS[l.reponse_categorie] || "—"})</p>
                      <p className="mt-1 line-clamp-3 whitespace-pre-wrap">{l.derniere_reponse || "—"}</p>
                    </div>
                    <div className="rounded-md border border-accent/40 bg-accent/5 p-3 text-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Résumé & action recommandée</p>
                      <p className="mt-1">{l.reponse_resume || "—"}</p>
                      {l.besoin && <p className="mt-0.5"><span className="text-muted-foreground">Besoin :</span> {l.besoin} {l.urgence && `· urgence ${l.urgence}`}</p>}
                      <p className="mt-1 font-medium">{l.action_recommandee} {l.delai_recommande && `· ${l.delai_recommande}`}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setDetail(l.id)} data-testid={`lead-detail-${l.id}`}>Historique complet</Button>
                    <Button size="sm" onClick={() => setClosing(l)} data-testid={`lead-close-${l.id}`}>
                      <CheckCircle2 size={14} className="mr-1.5" /> J'ai pris le relais
                    </Button>
                    {canAssign && (
                      <Select value={l.commercial_assigne_id || ""} onValueChange={(v) => assign(l, v)}>
                        <SelectTrigger className="h-8 w-[210px]" data-testid={`assign-select-${l.id}`}>
                          <UserPlus size={13} className="mr-1 shrink-0" />
                          <SelectValue placeholder="Attribuer à un commercial" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {l.commercial_assigne_nom && <span className="text-xs text-muted-foreground">Assigné : {l.commercial_assigne_nom}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {detail && <ProspectDetail prospectId={detail} onClose={() => setDetail(null)} onChanged={load} />}

      {closing && (
        <Dialog open onOpenChange={(o) => !o && setClosing(null)}>
          <DialogContent data-testid="close-lead-dialog">
            <DialogHeader><DialogTitle className="font-display">Prendre en main — {closing.raison_sociale}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nouveau statut du prospect</Label>
                <Select value={closeForm.nouveau_statut} onValueChange={(v) => setCloseForm({ ...closeForm, nouveau_statut: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="close-lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLOSE_STATUTS.map((s) => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}
                    <SelectItem value="lead_chaud">Lead chaud (garder actif)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Compte rendu (appel, e-mail envoyé…)</Label>
                <Textarea rows={3} className="mt-1.5" value={closeForm.commentaire} onChange={(e) => setCloseForm({ ...closeForm, commentaire: e.target.value })} data-testid="close-lead-comment-input" />
              </div>
              <Button onClick={closeLead} className="w-full" data-testid="close-lead-submit">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {draftFor && (
        <Dialog open onOpenChange={(o) => !o && setDraftFor(null)}>
          <DialogContent className="max-w-xl" data-testid="draft-dialog">
            <DialogHeader><DialogTitle className="font-display">Brouillon de réponse IA — {draftFor.raison_sociale}</DialogTitle></DialogHeader>
            {drafting ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Génération du brouillon par l'IA…</p>
            ) : draft ? (
              <div className="space-y-3">
                <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">Ce brouillon ne sera jamais envoyé automatiquement. Aucun tarif ni engagement n'y figure : relisez, ajustez et envoyez-le vous-même.</p>
                <div><Label>Objet</Label><Input className="mt-1" value={draft.sujet} onChange={(e) => setDraft({ ...draft, sujet: e.target.value })} data-testid="draft-subject-input" /></div>
                <div><Label>Message</Label><Textarea rows={8} className="mt-1" value={draft.corps} onChange={(e) => setDraft({ ...draft, corps: e.target.value })} data-testid="draft-body-input" /></div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => copyText(`${draft.sujet}\n\n${draft.corps}`, "Brouillon copié")} data-testid="draft-copy-button">
                    <Copy size={14} className="mr-1.5" /> Copier
                  </Button>
                  <Button asChild className="flex-1" data-testid="draft-mailto-button">
                    <a href={`mailto:${draftFor.contact_email || draftFor.email_generique}?subject=${encodeURIComponent(draft.sujet)}&body=${encodeURIComponent(draft.corps)}`}>
                      <Mail size={14} className="mr-1.5" /> Ouvrir dans ma messagerie
                    </a>
                  </Button>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      )}

      {oppFor && (
        <Dialog open onOpenChange={(o) => !o && setOppFor(null)}>
          <DialogContent data-testid="opportunity-dialog">
            <DialogHeader><DialogTitle className="font-display">Transformer en opportunité — {oppFor.raison_sociale}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Valeur potentielle (€)</Label>
                <Input type="number" min="0" className="mt-1.5" value={oppForm.valeur_potentielle} onChange={(e) => setOppForm({ ...oppForm, valeur_potentielle: e.target.value })} data-testid="opp-value-input" />
              </div>
              <div>
                <Label>Commentaire</Label>
                <Textarea rows={3} className="mt-1.5" value={oppForm.commentaire} onChange={(e) => setOppForm({ ...oppForm, commentaire: e.target.value })} data-testid="opp-comment-input" />
              </div>
              <Button onClick={createOpp} className="w-full bg-accent hover:bg-accent/90" data-testid="opp-submit-button">Créer l'opportunité</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
